import React, { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { notifyOperator } from "../utils/mail";
import { getFunctions, httpsCallable } from "firebase/functions";

// ★2026-10-10〜 会社・社員の登録（users / companies の作成）は Cloud Functions
//   （registerCompany / registerMember）が行う。画面から users を直接書くと、
//   権限（companyAdmin など）を自分で好きに書けてしまうため。ルールでも禁止している。
const fns = () => getFunctions(undefined, "us-central1");

// サーバーが「はっきり断った」エラー。この場合は作ったばかりのログイン用アカウントを消して、
// もう一度最初から登録できるようにする（already-exists は users が既にあるので消さない）。
const REJECTED = ["functions/invalid-argument", "functions/not-found", "functions/failed-precondition",
  "functions/permission-denied", "functions/unauthenticated"];

async function registerOrRollback(cred, name, payload) {
  try {
    return await httpsCallable(fns(), name)(payload);
  } catch (err) {
    if (REJECTED.includes(err.code)) {
      try { await cred.user.delete(); } catch (e) { console.warn("作りかけのアカウントを消せませんでした:", e); }
    }
    throw err;
  }
}

// URLパラメータで従業員モードか判定
const _p = new URLSearchParams(window.location.search);
const IS_EMPLOYEE = _p.get("type") === "employee";
// 管理者が発行した招待URLに含まれる会社コード
const INVITE_CODE = (_p.get("code") || "").trim().toUpperCase();

function Signup() {
  const [form, setForm] = useState({
    companyName: "", lastName: "", firstName: "", lastNameKana: "", firstNameKana: "",
    phone: "", email: "", password: "", companyCode: INVITE_CODE,
  });

  // 氏名は姓・名で受け取り、連結したものを name として保存する。
  // 既存の画面（勤怠・キオスク・給与明細など）は name を読んでいるため、
  // ここを残しておけば分割の影響が他へ波及しない。
  const fullName = () => [form.lastName.trim(), form.firstName.trim()].filter(Boolean).join(" ");
  const fullKana = () => [form.lastNameKana.trim(), form.firstNameKana.trim()].filter(Boolean).join(" ");
  const nameFields = () => ({
    name: fullName(),
    nameKana: fullKana(),
    lastName: form.lastName.trim(),
    firstName: form.firstName.trim(),
    lastNameKana: form.lastNameKana.trim(),
    firstNameKana: form.firstNameKana.trim(),
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // 招待URLで来たときに、どの会社への登録かを名前で示す。
  // null=確認中 / false=見つからない / オブジェクト=会社データ
  const [inviteCompany, setInviteCompany] = useState(null);

  useEffect(() => {
    if (!IS_EMPLOYEE || !INVITE_CODE) return;
    getDoc(doc(db, "companies", INVITE_CODE))
      .then(snap => setInviteCompany(snap.exists() ? snap.data() : false))
      .catch(err => {
        console.warn("招待された会社の取得に失敗しました:", err);
        setInviteCompany(false);
      });
  }, []);
  const [done, setDone] = useState(false);

  const handleChange = (e) => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) { setError("パスワードは6文字以上にしてください"); return; }
    setSubmitting(true);

    try {
      if (IS_EMPLOYEE) {
        // ===== 従業員登録 =====
        const code = form.companyCode.trim();
        const compSnap = await getDoc(doc(db, "companies", code));
        if (!compSnap.exists()) { setError("会社コードが正しくありません"); setSubmitting(false); return; }

        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);

        // 社員IDの採番・「最初の登録者か」の判定・users の作成は、すべてサーバー側で行う。
        const res = await registerOrRollback(cred, "registerMember", {
          companyId: code,
          ...nameFields(),
          phone: form.phone,
        });
        const memberNo = res.data.memberNo || "";
        const isFirst  = res.data.isFirst === true;
        // 登録後そのままログイン状態

        await notifyOperator({
          event_type:   "社員の新規登録",
          company_name: code,
          user_name:    fullName(),
          user_email:   form.email,
          detail:       `会社コード：${code}\n社員ID：${memberNo || "（未採番）"}\n電話：${form.phone || "（未入力）"}\n`
                      + `承認状態：${isFirst ? "自動承認（最初の登録者）" : "管理者の承認待ち"}`,
        });

      } else {
        // ===== 会社（管理者）登録 =====
        // 同じメールアドレスで会社を二重に登録させない
        try {
          const check = httpsCallable(fns(), "checkSignupEmail");
          const res = await check({ email: form.email.trim() });
          if (res.data.companyExists) {
            setError("このメールアドレスではすでに会社が登録されています。ログイン画面からお入りください。");
            return;
          }
        } catch (err) {
          // 確認できなくても登録は進める。重複はメールアドレスの一意性で防がれる。
          console.warn("メールアドレスの確認に失敗しました:", err);
        }

        const cred = await createUserWithEmailAndPassword(auth, form.email, form.password);

        // 会社コードの発行・会社と管理者（自分）の作成は、サーバー側で行う。
        const res = await registerOrRollback(cred, "registerCompany", {
          companyName: form.companyName,
          ...nameFields(),
          phone: form.phone,
        });
        const code = res.data.companyId;
        // 登録後そのままログイン状態

        await notifyOperator({
          event_type:   "会社の新規登録",
          company_name: `${form.companyName}（${code}）`,
          user_name:    fullName(),
          user_email:   form.email,
          detail:       `会社コード：${code}\n担当者：${fullName()}\n電話：${form.phone || "（未入力）"}\nプラン：お試し`,
        });
      }

      // 登録できたらアプリ本体へ移す。?signup=1 が付いたままだと
      // この画面が表示され続け、登録できていないように見えてしまう。
      window.location.replace("/");

    } catch (err) {
      if (err.code === "auth/email-already-in-use") {
        setError("このメールアドレスはすでに登録されています");
      } else if ([...REJECTED, "functions/already-exists"].includes(err.code) && err.message) {
        // サーバーが理由を日本語で返したときは、それをそのまま見せる
        setError(err.message);
      } else {
        setError("登録に失敗しました。もう一度お試しください。");
        console.error(err);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div style={st.container}>
        <div style={st.box}>
          <div style={{ fontSize: "48px", textAlign: "center", marginBottom: "16px" }}>✅</div>
          <h2 style={st.title}>登録申請完了</h2>
          <p style={{ textAlign: "center", color: "#7f8c8d", lineHeight: "1.7" }}>
            管理者の承認後にログインできます。
          </p>
          <a href="https://miyabicompany-manager.web.app" style={{ display: "block", textAlign: "center", marginTop: "24px", padding: "12px", backgroundColor: "#2c3e50", color: "#fff", borderRadius: "8px", textDecoration: "none" }}>
            ログイン画面へ
          </a>
        </div>
      </div>
    );
  }

  return (
    <div style={st.container}>
      <div style={st.box}>
        <h2 style={st.title}>miyabi Company</h2>
        <p style={st.subtitle}>{IS_EMPLOYEE ? "👤 従業員として登録する" : "🏢 会社の新規登録"}</p>

        <form onSubmit={handleSubmit} autoComplete="off">
          {!IS_EMPLOYEE && (
            <>
              <label style={st.label}>会社名・屋号</label>
              <input style={st.input} name="companyName" placeholder="株式会社みやび"
                autoComplete="off" value={form.companyName} onChange={handleChange} required />
            </>
          )}
          {IS_EMPLOYEE && (
            <>
              {INVITE_CODE && inviteCompany ? (
                // 招待URLから来た場合は、入力させずに登録先を見せる
                <div style={st.inviteBox}>
                  <div style={st.inviteLabel}>登録先の会社</div>
                  <div style={st.inviteName}>{inviteCompany.name}</div>
                  <div style={st.inviteCode}>会社コード：{INVITE_CODE}</div>
                </div>
              ) : (
                <>
                  <label style={st.label}>会社コード（管理者から取得）</label>
                  <input style={st.input} name="companyCode" placeholder="MYB-XXXXXX"
                    autoComplete="off" value={form.companyCode} onChange={handleChange} required />
                  {INVITE_CODE && inviteCompany === false && (
                    <p style={st.inviteWarn}>
                      このURLの会社コード「{INVITE_CODE}」が見つかりませんでした。
                      管理者に正しいURLをご確認いただくか、会社コードを直接入力してください。
                    </p>
                  )}
                </>
              )}
            </>
          )}

          <label style={st.label}>氏名</label>
          <div style={st.nameRow}>
            <input style={st.input} name="lastName" placeholder="姓（山田）"
              autoComplete="off" value={form.lastName} onChange={handleChange} required />
            <input style={st.input} name="firstName" placeholder="名（太郎）"
              autoComplete="off" value={form.firstName} onChange={handleChange} required />
          </div>
          <label style={st.label}>フリガナ</label>
          <div style={st.nameRow}>
            <input style={st.input} name="lastNameKana" placeholder="セイ（ヤマダ）"
              autoComplete="off" value={form.lastNameKana} onChange={handleChange} required />
            <input style={st.input} name="firstNameKana" placeholder="メイ（タロウ）"
              autoComplete="off" value={form.firstNameKana} onChange={handleChange} required />
          </div>

          <label style={st.label}>電話番号</label>
          <input style={st.input} name="phone" type="tel" placeholder="09012345678"
            autoComplete="off" value={form.phone} onChange={handleChange} required />

          <label style={st.label}>メールアドレス</label>
          <input style={st.input} type="email" name="email" placeholder="info@example.com"
            autoComplete="off" value={form.email} onChange={handleChange} required />

          <label style={st.label}>パスワード（6文字以上）</label>
          <input style={st.input} type="password" name="password" placeholder="••••••••"
            autoComplete="new-password" value={form.password} onChange={handleChange} required />

          {error && <p style={st.error}>{error}</p>}

          <button style={st.button} type="submit" disabled={submitting}>
            {submitting ? "登録中..." : "登録する"}
          </button>
        </form>

        <p style={{ textAlign: "center", marginTop: "20px", fontSize: "13px", color: "#999" }}>
          すでにアカウントをお持ちの方は
          <a href="https://miyabicompany-manager.web.app" style={{ color: "#2980b9", marginLeft: "4px" }}>
            ログイン
          </a>
        </p>

        {!IS_EMPLOYEE && (
          <div style={st.features}>
            <div style={st.feature}>✅ 最初は無料でお試し</div>
            <div style={st.feature}>✅ クレジットカード登録不要</div>
            <div style={st.feature}>✅ 出退勤・書類・給与明細を一元管理</div>
          </div>
        )}
      </div>
    </div>
  );
}

const st = {
  container: { display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh", backgroundColor: "#f0f4f8", padding: "20px" },
  box: { backgroundColor: "#fff", padding: "40px", borderRadius: "12px", boxShadow: "0 4px 20px rgba(0,0,0,0.1)", width: "400px", maxWidth: "100%" },
  title: { textAlign: "center", color: "#2c3e50", marginBottom: "4px" },
  subtitle: { textAlign: "center", color: "#7f8c8d", marginBottom: "28px" },
  label: { display: "block", fontSize: "13px", color: "#2c3e50", fontWeight: "bold", marginBottom: "6px" },
  inviteBox:   { backgroundColor: "#eaf4ff", border: "1px solid #bcdcff", borderRadius: "8px", padding: "14px 16px", marginBottom: "16px" },
  inviteLabel: { fontSize: "12px", color: "#5a7a99", marginBottom: "4px" },
  inviteName:  { fontSize: "17px", fontWeight: "bold", color: "#2c3e50" },
  inviteCode:  { fontSize: "12px", color: "#7f8c8d", marginTop: "4px" },
  inviteWarn:  { fontSize: "12px", color: "#8a5a00", backgroundColor: "#fff6e5", border: "1px solid #f5c26b", borderRadius: "8px", padding: "10px 12px", lineHeight: "1.8", marginTop: "8px" },
  input: { width: "100%", padding: "12px", marginBottom: "16px", border: "1px solid #ddd", borderRadius: "8px", fontSize: "15px", boxSizing: "border-box" },
  nameRow: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "10px" },
  button: { width: "100%", padding: "14px", backgroundColor: "#2c3e50", color: "#fff", border: "none", borderRadius: "8px", fontSize: "16px", cursor: "pointer", marginTop: "4px" },
  error: { color: "#e74c3c", fontSize: "14px", marginBottom: "8px" },
  features: { marginTop: "24px", borderTop: "1px solid #eee", paddingTop: "20px" },
  feature: { fontSize: "13px", color: "#27ae60", marginBottom: "8px" },
};

export default Signup;
